import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex items-start justify-between gap-4" }
const _hoisted_2 = { class: "mt-1 text-sm font-medium text-[#64748b]" }
const _hoisted_3 = { class: "min-h-0 overflow-y-auto bg-[#fbfdff] px-7 py-6" }
const _hoisted_4 = { class: "rounded-[1.25rem] border border-[rgba(219,227,239,0.95)] bg-white shadow-[0_10px_30px_rgba(57,84,120,0.06)]" }
const _hoisted_5 = { class: "border-b border-[rgba(230,236,245,1)] px-5 py-5" }
const _hoisted_6 = { class: "flex items-start gap-3" }
const _hoisted_7 = { class: "flex h-10 w-10 shrink-0 items-center justify-center rounded-[0.85rem] bg-blue-100 text-lg text-blue-700" }
const _hoisted_8 = { class: "mx-5 my-5 flex items-center justify-between gap-3 rounded-[1rem] border border-blue-100 bg-gradient-to-r from-[#eff6ff] to-[#f7fbff] p-4" }
const _hoisted_9 = { class: "text-sm font-extrabold text-[#172033]" }
const _hoisted_10 = { "data-testid": "renewal-term-label" }
const _hoisted_11 = { class: "flex items-center gap-2" }
const _hoisted_12 = { class: "grid gap-x-5 gap-y-4 px-5 pb-5 sm:grid-cols-2" }
const _hoisted_13 = { class: "flex flex-col gap-[0.42rem]" }
const _hoisted_14 = { class: "flex flex-col gap-[0.42rem]" }
const _hoisted_15 = { class: "flex flex-col gap-[0.42rem]" }
const _hoisted_16 = { class: "flex flex-col gap-[0.42rem]" }
const _hoisted_17 = { class: "flex flex-col gap-[0.42rem]" }
const _hoisted_18 = { class: "flex flex-col gap-[0.42rem]" }
const _hoisted_19 = { class: "flex flex-col gap-[0.42rem]" }
const _hoisted_20 = ["value"]
const _hoisted_21 = ["value"]
const _hoisted_22 = { class: "flex flex-col gap-[0.42rem] sm:col-span-2" }
const _hoisted_23 = {
  key: 0,
  class: "mx-5 mb-5 rounded-[0.8rem] border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600"
}

import { computed, reactive, ref, watch } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { useToast } from "vue-toast-notification";
import { Minus, Plus, RefreshCw } from "lucide-vue-next";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import type { NormalizedClient } from "@/composables/useClientsQuery";
import { updateClient, type ClientAccountStatus } from "@/services/clientService";
import { useAuthStore } from "@/store/auth";
import RenewalDateInput from "@/components/dashboard/renewals/RenewalDateInput.vue";
import {
  addCalendarMonths,
  calculateRenewalSchedule,
  inferRenewalTermMonths,
} from "@/utils/renewalSchedule";

const MIN_RENEWAL_TERM_MONTHS = 1;


export default /*@__PURE__*/_defineComponent({
  __name: 'RenewalEditModal',
  props: {
    open: { type: Boolean },
    client: {},
    renewalNote: {},
    accountStatus: {},
    accountStatusOptions: {}
  },
  emits: ["update:open"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const authStore = useAuthStore();
const queryClient = useQueryClient();
const toast = useToast();
const isSaving = ref(false);
const formError = ref("");
const renewalTermMonths = ref(3);
const renewalStartWasEdited = ref(false);
const initialAccountStatus = ref("");
const hasLegacyAccountStatus = computed(() => !props.accountStatusOptions.includes(
  initialAccountStatus.value as ClientAccountStatus
));

const form = reactive({
  onboarding_date: "",
  subscription_start_date: "",
  renewal_date: "",
  payment_due_date: "",
  renewal_start_date: "",
  renewal_end_date: "",
  client_account_status: "",
  renewal_note: "",
});

function normalizeDateInput(value: unknown) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) return rawValue.slice(0, 10);

  const parsed = new Date(rawValue);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
}

function renewalValue(client: NormalizedClient, field: string) {
  return client.raw?.[field] || client.raw?.renewal_summary?.[field] || "";
}

function applyCalculatedSchedule() {
  const schedule = calculateRenewalSchedule(
    form.subscription_start_date,
    renewalTermMonths.value
  );
  if (!schedule.renewalDate) return;

  // Keep a manually chosen renewal start as the anchor. Opening the form never
  // generates dates, and unrelated controls do not create a missing window.
  if (form.renewal_start_date && !renewalStartWasEdited.value) {
    form.renewal_start_date = schedule.renewalStartDate;
  }
  if (form.renewal_end_date) {
    form.renewal_end_date = renewalStartWasEdited.value
      ? addCalendarMonths(form.renewal_start_date, renewalTermMonths.value)
      : schedule.renewalEndDate;
  }
  form.renewal_date = schedule.renewalDate;
}

function handleSubscriptionStartChange() {
  applyCalculatedSchedule();
}

function handleRenewalStartChange() {
  renewalStartWasEdited.value = true;
  form.renewal_end_date = addCalendarMonths(
    form.renewal_start_date,
    renewalTermMonths.value
  );
}

function adjustMonths(months: number) {
  if (!form.subscription_start_date && !form.renewal_start_date) {
    formError.value = "Set a subscription start or renewal start date before adjusting the renewal term.";
    return;
  }

  const nextTermMonths = renewalTermMonths.value + months;
  if (nextTermMonths < MIN_RENEWAL_TERM_MONTHS) return;

  renewalTermMonths.value = nextTermMonths;
  formError.value = "";
  if (renewalStartWasEdited.value || (form.renewal_start_date && form.renewal_end_date)) {
    handleRenewalStartChange();
  }
  else applyCalculatedSchedule();
}

function addMonths() {
  adjustMonths(1);
}

function subtractMonths() {
  adjustMonths(-1);
}

function hydrateForm(client: NormalizedClient | null, renewalNote?: string | null) {
  if (!client) return;
  renewalStartWasEdited.value = false;

  form.onboarding_date = normalizeDateInput(
    renewalValue(client, "onboarding_date") || client.onboardingDateValue
  );
  form.subscription_start_date = normalizeDateInput(
    renewalValue(client, "subscription_start_date") ||
      client.subscriptionStartDateValue
  );
  form.renewal_date = normalizeDateInput(
    renewalValue(client, "renewal_date") || client.renewalDateValue
  );
  form.payment_due_date = normalizeDateInput(
    renewalValue(client, "payment_due_date") || client.paymentDueDateValue
  );
  form.renewal_start_date = normalizeDateInput(
    renewalValue(client, "renewal_start_date") || client.renewalStartDateValue
  );
  form.renewal_end_date = normalizeDateInput(
    renewalValue(client, "renewal_end_date") || client.renewalEndDateValue
  );
  initialAccountStatus.value = String(
    props.accountStatus ?? client.raw?.client_account_status ?? client.clientAccountStatus ?? "Active"
  ).trim() || "Active";
  form.client_account_status = initialAccountStatus.value;
  form.renewal_note = String(
    renewalNote ?? client.raw?.renewal_note ?? ""
  ).trim();
  renewalTermMonths.value = inferRenewalTermMonths(
    form.renewal_start_date && form.renewal_end_date
      ? form.renewal_start_date : form.subscription_start_date,
    form.renewal_start_date && form.renewal_end_date
      ? form.renewal_end_date : form.renewal_date || form.renewal_end_date,
    3
  );
  formError.value = "";
}

watch(
  [() => props.client, () => props.open, () => props.renewalNote, () => props.accountStatus],
  ([client, open, renewalNote]) => {
    if (client && open) {
      hydrateForm(client, renewalNote);
    }
  },
  { immediate: true }
);

function validateForm() {
  if (form.client_account_status !== initialAccountStatus.value
    && !props.accountStatusOptions.includes(form.client_account_status as ClientAccountStatus)) {
    formError.value = "Select a valid account status.";
    return false;
  }
  if (form.renewal_note.length > 1000) {
    formError.value = "Renewal note must be 1,000 characters or fewer.";
    return false;
  }

  if (
    form.renewal_start_date &&
    form.renewal_end_date &&
    form.renewal_end_date < form.renewal_start_date
  ) {
    formError.value = "Renewal end must be on or after renewal start.";
    return false;
  }

  formError.value = "";
  return true;
}

function getErrorMessage(error: unknown) {
  const apiError = error as {
    response?: { data?: { message?: string } };
  };
  return (
    apiError.response?.data?.message ||
    "Renewal details could not be saved right now."
  );
}

async function saveRenewal() {
  if (isSaving.value || !props.client || !authStore.canEditRenewalTracking || !validateForm()) {
    return;
  }

  isSaving.value = true;

  try {
    await updateClient(props.client.raw?.id || props.client.id, {
      onboarding_date: form.onboarding_date || null,
      subscription_start_date: form.subscription_start_date || null,
      renewal_date: form.renewal_date || null,
      payment_due_date: form.payment_due_date || null,
      renewal_start_date: form.renewal_start_date || null,
      renewal_end_date: form.renewal_end_date || null,
      ...(form.client_account_status !== initialAccountStatus.value ? {
        client_account_status: form.client_account_status as ClientAccountStatus,
      } : {}),
      renewal_note: form.renewal_note.trim() || null,
    });

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["clients"] }),
      queryClient.invalidateQueries({ queryKey: ["renewal-tracking-client"] }),
    ]);

    toast.success("Renewal details updated.");
    emit("update:open", false);
  } catch (error) {
    toast.error(getErrorMessage(error));
  } finally {
    isSaving.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    open: __props.open,
    "onUpdate:open": _cache[9] || (_cache[9] = ($event: any) => (emit('update:open', $event)))
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(DialogContent), { class: "max-w-3xl max-h-[90vh] rounded-[1.5rem] border-0 bg-white p-0 shadow-[0_32px_80px_rgba(23,32,51,0.2)] overflow-hidden" }, {
        default: _withCtx(() => [
          _createElementVNode("form", {
            class: "flex max-h-[90vh] flex-col",
            onSubmit: _withModifiers(saveRenewal, ["prevent"])
          }, [
            _createVNode(_unref(DialogHeader), { class: "shrink-0 border-b border-[rgba(224,230,240,1)] bg-gradient-to-br from-[#f7fbff] via-white to-[#eef6ff] px-7 py-6" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_1, [
                  _createElementVNode("div", null, [
                    _cache[11] || (_cache[11] = _createElementVNode("p", { class: "text-[0.68rem] font-extrabold uppercase tracking-[0.16em] text-blue-600" }, " Renewal workspace ", -1)),
                    _createVNode(_unref(DialogTitle), { class: "mt-1 text-[1.45rem] font-extrabold tracking-[-0.02em] text-[#172033]" }, {
                      default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                        _createTextVNode(" Edit Renewal ", -1)
                      ]))]),
                      _: 1
                    }),
                    _createElementVNode("p", _hoisted_2, _toDisplayString(__props.client?.companyName || "Account"), 1)
                  ]),
                  _cache[12] || (_cache[12] = _createElementVNode("div", { class: "hidden items-center gap-2 rounded-full border border-emerald-200 bg-white/80 px-3 py-1.5 text-xs font-bold text-emerald-700 sm:flex" }, [
                    _createElementVNode("span", { class: "h-2 w-2 rounded-full bg-emerald-500" }),
                    _createTextVNode(" Live record ")
                  ], -1))
                ])
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("section", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, [
                      _createVNode(_unref(RefreshCw), { class: "h-5 w-5" })
                    ]),
                    _cache[13] || (_cache[13] = _createElementVNode("div", null, [
                      _createElementVNode("p", { class: "text-[1rem] font-extrabold text-[#172033]" }, " Renewal Tracking "),
                      _createElementVNode("p", { class: "mt-1 max-w-xl text-[0.8rem] leading-5 text-[#64748b]" }, " Keep subscription dates, payment timing, and renewal notes aligned for this account. ")
                    ], -1))
                  ])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", null, [
                    _createElementVNode("p", _hoisted_9, [
                      _createElementVNode("span", _hoisted_10, _toDisplayString(renewalTermMonths.value) + " month" + _toDisplayString(renewalTermMonths.value === 1 ? "" : "s"), 1)
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_11, [
                    _createVNode(_unref(Button), {
                      type: "button",
                      variant: "outline",
                      size: "icon",
                      disabled: renewalTermMonths.value <= MIN_RENEWAL_TERM_MONTHS,
                      class: "h-10 w-10 shrink-0 border-blue-200 bg-white text-blue-700 shadow-sm hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50",
                      "aria-label": "Remove one month from renewal term",
                      "data-testid": "renewal-subtract-month",
                      onClick: subtractMonths
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Minus), { class: "h-4 w-4" })
                      ]),
                      _: 1
                    }, 8, ["disabled"]),
                    _createVNode(_unref(Button), {
                      type: "button",
                      variant: "outline",
                      size: "icon",
                      class: "h-10 w-10 shrink-0 border-blue-200 bg-white text-blue-700 shadow-sm hover:border-blue-300 hover:bg-blue-50",
                      "aria-label": "Add one month to renewal term",
                      "data-testid": "renewal-add-month",
                      onClick: addMonths
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Plus), { class: "h-4 w-4" })
                      ]),
                      _: 1
                    })
                  ])
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _cache[22] || (_cache[22] = _createElementVNode("div", { class: "sm:col-span-2" }, [
                    _createElementVNode("p", { class: "text-[0.68rem] font-extrabold uppercase tracking-[0.13em] text-[#8a97aa]" }, " Account timeline ")
                  ], -1)),
                  _createElementVNode("label", _hoisted_13, [
                    _cache[14] || (_cache[14] = _createElementVNode("span", { class: "text-[0.8rem] font-bold text-[#64748b]" }, " Onboarding date ", -1)),
                    _createVNode(RenewalDateInput, {
                      modelValue: form.onboarding_date,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.onboarding_date) = $event)),
                      type: "date",
                      "data-testid": "renewal-onboarding-date",
                      class: "min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]"
                    }, null, 8, ["modelValue"])
                  ]),
                  _createElementVNode("label", _hoisted_14, [
                    _cache[15] || (_cache[15] = _createElementVNode("span", { class: "text-[0.8rem] font-bold text-[#64748b]" }, " Subscription start ", -1)),
                    _createVNode(RenewalDateInput, {
                      modelValue: form.subscription_start_date,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.subscription_start_date) = $event)),
                      type: "date",
                      "data-testid": "renewal-subscription-start",
                      class: "min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]",
                      onChange: handleSubscriptionStartChange
                    }, null, 8, ["modelValue"])
                  ]),
                  _cache[23] || (_cache[23] = _createElementVNode("div", { class: "sm:col-span-2 mt-1 flex items-center gap-3 border-t border-[rgba(230,236,245,1)] pt-4" }, [
                    _createElementVNode("p", { class: "text-[0.68rem] font-extrabold uppercase tracking-[0.13em] text-[#8a97aa]" }, " Renewal schedule "),
                    _createElementVNode("span", { class: "h-px flex-1 bg-[rgba(230,236,245,1)]" })
                  ], -1)),
                  _createElementVNode("div", _hoisted_15, [
                    _cache[16] || (_cache[16] = _createElementVNode("span", { class: "text-[0.8rem] font-bold text-[#64748b]" }, "Renewal date", -1)),
                    _createVNode(RenewalDateInput, {
                      modelValue: form.renewal_date,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.renewal_date) = $event)),
                      type: "date",
                      "data-testid": "renewal-date",
                      class: "min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]"
                    }, null, 8, ["modelValue"])
                  ]),
                  _createElementVNode("label", _hoisted_16, [
                    _cache[17] || (_cache[17] = _createElementVNode("span", { class: "text-[0.8rem] font-bold text-[#64748b]" }, "Payment due date", -1)),
                    _createVNode(RenewalDateInput, {
                      modelValue: form.payment_due_date,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.payment_due_date) = $event)),
                      type: "date",
                      "data-testid": "renewal-payment-due-date",
                      class: "min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]"
                    }, null, 8, ["modelValue"])
                  ]),
                  _createElementVNode("label", _hoisted_17, [
                    _cache[18] || (_cache[18] = _createElementVNode("span", { class: "text-[0.8rem] font-bold text-[#64748b]" }, "Renewal start", -1)),
                    _createVNode(RenewalDateInput, {
                      modelValue: form.renewal_start_date,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.renewal_start_date) = $event)),
                      type: "date",
                      "data-testid": "renewal-start-date",
                      class: "min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]",
                      onChange: handleRenewalStartChange
                    }, null, 8, ["modelValue"])
                  ]),
                  _createElementVNode("label", _hoisted_18, [
                    _cache[19] || (_cache[19] = _createElementVNode("span", { class: "text-[0.8rem] font-bold text-[#64748b]" }, "Renewal end", -1)),
                    _createVNode(RenewalDateInput, {
                      modelValue: form.renewal_end_date,
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.renewal_end_date) = $event)),
                      type: "date",
                      "data-testid": "renewal-end-date",
                      class: "min-h-[2.75rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049]"
                    }, null, 8, ["modelValue"])
                  ]),
                  _cache[24] || (_cache[24] = _createElementVNode("div", { class: "sm:col-span-2 mt-1 flex items-center gap-3 border-t border-[rgba(230,236,245,1)] pt-4" }, [
                    _createElementVNode("p", { class: "text-[0.68rem] font-extrabold uppercase tracking-[0.13em] text-[#8a97aa]" }, " Account status and notes "),
                    _createElementVNode("span", { class: "h-px flex-1 bg-[rgba(230,236,245,1)]" })
                  ], -1)),
                  _createElementVNode("label", _hoisted_19, [
                    _cache[20] || (_cache[20] = _createElementVNode("span", { class: "text-[0.8rem] font-bold text-[#64748b]" }, "Account status", -1)),
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.client_account_status) = $event)),
                      "aria-label": "Account status",
                      class: "min-h-[2.75rem] rounded-[0.95rem] border border-[rgba(211,220,233,1)] bg-white px-3 text-sm font-semibold text-[#243049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#8eb4ff]"
                    }, [
                      (hasLegacyAccountStatus.value)
                        ? (_openBlock(), _createElementBlock("option", {
                            key: 0,
                            value: initialAccountStatus.value,
                            disabled: ""
                          }, _toDisplayString(initialAccountStatus.value) + " (current) ", 9, _hoisted_20))
                        : _createCommentVNode("", true),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.accountStatusOptions, (status) => {
                        return (_openBlock(), _createElementBlock("option", {
                          key: status,
                          value: status
                        }, _toDisplayString(status), 9, _hoisted_21))
                      }), 128))
                    ], 512), [
                      [_vModelSelect, form.client_account_status]
                    ])
                  ]),
                  _createElementVNode("label", _hoisted_22, [
                    _cache[21] || (_cache[21] = _createElementVNode("span", { class: "text-[0.8rem] font-bold text-[#64748b]" }, "Renewal note", -1)),
                    _createVNode(_unref(Textarea), {
                      modelValue: form.renewal_note,
                      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.renewal_note) = $event)),
                      rows: "4",
                      maxlength: "1000",
                      placeholder: "Record a payment confirmation, follow-up, or renewal note.",
                      class: "min-h-[6.5rem] rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049] shadow-sm"
                    }, null, 8, ["modelValue"])
                  ])
                ]),
                (formError.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_23, _toDisplayString(formError.value), 1))
                  : _createCommentVNode("", true)
              ])
            ]),
            _createVNode(_unref(DialogFooter), { class: "shrink-0 gap-2 border-t border-[rgba(224,230,240,1)] bg-white px-7 py-5" }, {
              default: _withCtx(() => [
                _createVNode(_unref(Button), {
                  type: "button",
                  variant: "outline",
                  disabled: isSaving.value,
                  onClick: _cache[8] || (_cache[8] = ($event: any) => (emit('update:open', false)))
                }, {
                  default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                    _createTextVNode(" Cancel ", -1)
                  ]))]),
                  _: 1
                }, 8, ["disabled"]),
                _createVNode(_unref(Button), {
                  type: "submit",
                  disabled: isSaving.value || !_unref(authStore).canEditRenewalTracking,
                  class: "bg-blue-600 text-white hover:bg-blue-700"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(isSaving.value ? "Saving..." : "Save renewal"), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ]),
              _: 1
            })
          ], 32)
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})